import * as React from 'react';

import { translate } from '~l10n/client';
import Backbone from 'backbone';
import cx from 'classnames';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { SmallButton } from '~shared/components/buttons';
import { IconExternal } from '~shared/components/icons/standard';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItemLink,
  MenuItemLinkRenderPropArgs,
  MenuItemRenderPropArgs,
  MenuList,
} from '~shared/components/Menu';
import { useAccount } from '~teacher/hook/account';

const Constants = require('Constants');

type StyledItemProps = { children: React.ReactNode } & (
  | MenuItemRenderPropArgs
  | MenuItemLinkRenderPropArgs
);

function StyledMenuItem({ children, isActive, ...props }: StyledItemProps) {
  const className = cx(
    'flex w-full px-10 py-3 whitespace-no-wrap cursor-pointer',
    'font-sans text-base leading-8 text-left border-0 rounded-1 text-blue-900',
    ' no-underline',
    isActive ? 'bg-cyan-50' : 'bg-white'
  );

  const Component = 'href' in props ? 'a' : 'button';

  return (
    <div className="px-2">
      <Component
        type={Component === 'button' ? 'button' : undefined}
        className={className}
        {...props}
      >
        {children}
      </Component>
    </div>
  );
}

interface ProfileMenuProps {
  isVisible: boolean;
  handleSignOut: () => void;
}

function firstAndLastInitial(...names: string[]) {
  return names
    .filter(Boolean)
    .map((name) => name[0])
    .map((a) => a.toUpperCase())
    .join('');
}

export function ProfileMenu({ isVisible, handleSignOut }: ProfileMenuProps) {
  const firstLastNameQuery = useAccount({
    select: ({ firstName, lastName }) => ({ firstName, lastName }),
  });
  if (!isVisible) return null;
  const ariaLabel = `Profile Menu, ${
    firstLastNameQuery.data?.firstName ?? ''
  } 4 items`;

  const handleMenuItemClick = (selectedItem: string) => {
    Backbone.history.navigate(selectedItem, { trigger: true });
  };

  return (
    <div data-testid="profile-menu" className="hidden tb:block">
      <Menu>
        <MenuButton>
          {({ props }) => (
            <SmallButton
              {...props}
              a11yLabel={ariaLabel}
              className="text-sm font-600"
            >
              {firstAndLastInitial(
                ...Object.values(firstLastNameQuery.data ?? {})
              )}
            </SmallButton>
          )}
        </MenuButton>
        <MenuList className="absolute right-0 py-3 bg-white rounded-2 outline-none shadow-menu">
          <MenuItem onClick={() => {}}>
            {() => (
              <div className="px-2">
                <div className="flex w-full px-10 py-3 whitespace-no-wrap rounded-1 no-underline ">
                  <h3 className="font-sans text-xs font-700 leading-8 text-grey-900 tracking-wider uppercase m-0 bg-white truncate">
                    {firstLastNameQuery.data?.firstName}
                  </h3>
                </div>
              </div>
            )}
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick(Constants.PROFILE_INFO)}>
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                {translate('Profile')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem
            onClick={() => {
              new FeatureUseEventAdapter({
                feature: 'plans_and_members_menu',
                action: 'click',
              }).sendAnalytics();
              handleMenuItemClick(Constants.PROFILE_BILLING);
            }}
          >
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                {translate('Plan and Members')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItemLink href="https://help.socrative.com" target="_blank">
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                <span className="mr-8">{translate('Help Topics')}</span>
                <IconExternal
                  title={translate('External Link')}
                  titleId="help-topics-external-link"
                />
              </StyledMenuItem>
            )}
          </MenuItemLink>
          <MenuItem>
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                <span id="contact-intercom-support">
                  {translate('Contact') + ' Socrative'}
                </span>
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem onClick={handleSignOut}>
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                {translate('Sign Out')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem>
            {() => (
              <span className="flex w-full px-12 py-3 text-grey-800 whitespace-no-wrap">{`v${window.APP_CONFIG.VERSION}`}</span>
            )}
          </MenuItem>
        </MenuList>
      </Menu>
    </div>
  );
}
